import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';

import { EMPLOYEE_ROOT } from '@/hooks/employee-portal';
import {
  getSnapshot,
  installQueueListeners,
  setUploadSuccessListener,
  subscribe,
  type QueuedUpload,
} from '@/lib/upload-queue';

/*
 * React's view of the upload queue — ROADMAP.md §6.4.
 *
 * The queue itself is module state on purpose (see `lib/upload-queue.ts`): it
 * has to outlive every component, because moving from the job screen to the
 * dashboard must not abandon a photo mid-flight. `useSyncExternalStore` is the
 * supported way to read state that lives outside React, and it is why the queue
 * hands out a new array on every change rather than mutating in place.
 */

/** Everything currently queued, re-rendering the caller when it changes. */
export function useUploadQueue(): readonly QueuedUpload[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Just this job's, for the checklist and the job screen's banner. */
export function useUploadsForJob(jobId: string): QueuedUpload[] {
  const all = useUploadQueue();
  return all.filter((entry) => entry.jobId === jobId);
}

/**
 * Connect the queue to the app. **Mounted once, in the employee layout.**
 *
 * Two wirings, and both belong here rather than in the queue: the queue should
 * not know what TanStack Query is, and it should not attach window listeners
 * that nothing ever removes.
 */
export function useUploadQueueRuntime(): void {
  const queryClient = useQueryClient();

  useEffect(() => installQueueListeners(), []);

  useEffect(
    () =>
      setUploadSuccessListener(() => {
        /*
         * Invalidate rather than write the response into the cache. An upload
         * that lands while the cleaner is looking at a *different* job would
         * otherwise write that job's detail over whatever key was asked for,
         * and the counts on the dashboard would still be stale. One
         * invalidation of the portal's root is cheap and cannot be wrong.
         */
        void queryClient.invalidateQueries({ queryKey: EMPLOYEE_ROOT });
      }),
    [queryClient],
  );
}
